import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ChevronRight, Check } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import FormBuilder from '@/components/company/FormBuilder';
import type { FormField } from '@/components/company/FormBuilder';
import useCompanyState from '@/states/company.state';
import useTenantState from '@/states/tenant.state';
import { useIsReadOnly } from '@/utils/auth/roleChecker';
import { useCompanyProfile, useUpdateCompanyField } from '@/hooks/useCompanyProfile';
import { PositioningSkeleton } from '@/components/company/skeletons';
import { useGetCompanyData } from './company-data/useCompanyData';
import { useCompanyProfileSteps } from '@/hooks/useCompanyProfileSteps';
import SwitchButton from '@/components/company/SwitchButton';
import StepTipsPanel from '@/components/company/StepTipsPanel';
import { useAutosave } from '@/hooks/useAutosave';
import DraftBanner from '@/components/form/DraftBanner';
import AutosaveIndicator from '@/components/form/AutosaveIndicator';

interface PositioningFormValues {
  company: string;
  category: string;
  dna: string;
  core_value: string;
  add_value: string;
  is_auto_generate: boolean;
  positioning: string;
}

export default function Positioning() {
  const formControl = useForm<PositioningFormValues>({
    defaultValues: {
      company: '',
      category: '',
      dna: '',
      core_value: '',
      add_value: '',
      is_auto_generate: true,
      positioning: '',
    },
  });
  const { handleSubmit, reset, setValue } = formControl;
  const formRef = useRef<HTMLFormElement>(null);
  const watched = formControl.watch();
  const navigate = useNavigate();
  const { tenant_id } = useParams();

  const { currentTenant } = useTenantState();
  const isReadOnly = useIsReadOnly();
  const { companyProfile } = useCompanyState();
  const { isLoading } = useCompanyProfile(currentTenant?.id);
  const updateFieldMutation = useUpdateCompanyField();
  const { data: companyData } = useGetCompanyData(currentTenant?.id);
  const [isPublished, setIsPublished] = useState(false);
  const [showPanel, setShowPanel] = useState(true);

  useCompanyProfileSteps();
  const { companyProfileSteps } = useCompanyState();
  const positioningStep = companyProfileSteps.find(step => step.step_name.trim().toLowerCase() === 'positioning');
  const videoLink = positioningStep?.video_link;
  const tips = (positioningStep as { step_name: string; video_link?: string; tips?: string } | undefined)?.tips;

  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const autosave = useAutosave({
    form: formControl,
    formKey: 'positioning',
    enabled: isDataLoaded && !isReadOnly,
    // is_auto_generate is intentionally INCLUDED in the draft. The effect below
    // regenerates `positioning` from the source fields only while that flag is
    // true, so a draft of a manually written statement must carry the flag that
    // keeps it from being overwritten on restore.
  });

  const hasPendingDraft = !!autosave.pendingDraft;

  // Load existing positioning data from company profile
  useEffect(() => {
    if (isLoading) return;

    if (!companyProfile) {
      setIsDataLoaded(true);
      return;
    }

    const positioningData = Array.isArray(companyProfile.positioning) ? companyProfile.positioning[0] : null;

    if (positioningData) {
      const rawCoreValue = ((positioningData as Record<string, unknown>).core_value as string) ?? '';
      const hasAddValue = rawCoreValue.includes('[ADD_VALUE]');
      const coreValue = hasAddValue ? rawCoreValue.split('[ADD_VALUE]')[0].trim() : rawCoreValue;
      const addValueFromCore = hasAddValue ? rawCoreValue.split('[ADD_VALUE]')[1].trim() : '';
      const addValue = addValueFromCore || ((positioningData as Record<string, unknown>).add_value as string) || '';

      const company = ((positioningData as Record<string, unknown>).company as string) ?? '';
      const category = ((positioningData as Record<string, unknown>).category as string) ?? '';
      const dna = ((positioningData as Record<string, unknown>).dna as string) ?? '';
      const savedPositioning = ((positioningData as Record<string, unknown>).positioning as string) ?? '';

      const generated = `${company || '[perusahaan]'} adalah ${category || '[category]'} yang menjual ${
        dna || '[produk/jasa]'
      } yang ${coreValue || '[core value]'} dan ${addValue || '[add value]'}`;

      let isAutoGenerate = true;
      if (savedPositioning && savedPositioning !== generated) {
        isAutoGenerate = false; // It was manually edited
      }

      reset({
        company,
        category,
        dna,
        core_value: coreValue,
        add_value: addValue,
        is_auto_generate: isAutoGenerate,
        positioning: savedPositioning,
      });
    }

    setIsPublished(!!(companyProfile as Record<string, unknown>).is_published_positioning);
    setIsDataLoaded(true);
  }, [isLoading, companyProfile, reset]);

  // Auto-fill company and category from company-data if positioning data is empty
  useEffect(() => {
    if (!companyData) return;

    const currentCompany = formControl.getValues('company');
    const currentCategory = formControl.getValues('category');

    if (!currentCompany && companyData.business_name) {
      setValue('company', companyData.business_name);
    }
    const companyBusinessCategory = companyData.business_category || companyData.business_type;
    if (!currentCategory && companyBusinessCategory) {
      setValue('category', companyBusinessCategory);
    }
    // Depend on the primitive fields, not the companyData object whose identity can change
    // every render and would re-run this effect in an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyData?.business_name, companyData?.business_category, companyData?.business_type, setValue]);

  // Auto-regenerate the positioning sentence whenever any of the source
  // inputs change, UNLESS the user has turned off the toggle.
  useEffect(() => {
    if (!isDataLoaded) return;
    // While a draft is being offered, the form still shows server values.
    // Regenerating now would overwrite `positioning` before the user has
    // chosen, and the restore would then be compared against mutated state.
    if (hasPendingDraft) return;
    if (!watched.is_auto_generate) return;

    const company = watched.company || '[perusahaan]';
    const category = watched.category || '[category]';
    const dna = watched.dna || '[produk/jasa]';
    const core = watched.core_value || '[core value]';
    const add = watched.add_value || '[add value]';

    const generated = `${company} adalah ${category} yang menjual ${dna} yang ${core} dan ${add}`;

    setValue('positioning', generated, { shouldDirty: false });
  }, [
    watched.company,
    watched.category,
    watched.dna,
    watched.core_value,
    watched.add_value,
    watched.is_auto_generate,
    isDataLoaded,
    hasPendingDraft,
    setValue,
  ]);

  const handleSave = async (data: PositioningFormValues) => {
    if (!currentTenant?.id) return;

    // Offline: the request would fail and lose the edits, so hold them locally
    // and let the user retry once the connection is back.
    if (!autosave.isOnline) {
      autosave.flush({ pendingSync: true });
      toast.info('Anda sedang offline. Perubahan disimpan sebagai draft di perangkat ini.');
      return;
    }

    const payload = [
      {
        company: data.company,
        category: data.category,
        dna: data.dna,
        core_value: data.add_value ? `${data.core_value} [ADD_VALUE] ${data.add_value}` : data.core_value,
        add_value: data.add_value,
        positioning: data.positioning,
      },
    ];

    await updateFieldMutation.mutateAsync({
      tenantId: currentTenant.id,
      field: 'positioning',
      value: payload,
      isPublished: isPublished,
    });
    // Server now holds these values; the draft would only be noise on return.
    autosave.clearDraft();
    toast.success('Positioning berhasil disimpan');

    if (tenant_id) {
      navigate(`/${tenant_id}/vision`);
    } else if (currentTenant?.slug || currentTenant?.id) {
      navigate(`/${currentTenant.slug || currentTenant.id}/vision`);
    }
  };

  if (isLoading) {
    return <PositioningSkeleton />;
  }

  const isSaving = updateFieldMutation.isLoading;

  const fields: FormField[] = [
    {
      name: 'company',
      label: 'Nama Perusahaan',
      type: 'text',
      placeholder: 'Contoh: Bakso Fedas',
      required: true,
    },
    {
      name: 'category',
      label: 'Kategori Bisnis',
      type: 'text',
      placeholder: 'Contoh: Warung Makan',
      required: true,
    },
    {
      name: 'dna',
      label: 'DNA',
      type: 'text',
      placeholder: 'Contoh: Bakso',
      required: true,
    },
    {
      name: 'core_value',
      label: 'Core Value',
      type: 'text',
      placeholder: 'Contoh: Pedas',
      required: true,
    },
    {
      name: 'add_value',
      label: 'Add Value',
      type: 'text',
      placeholder: 'Contoh: Cepat',
    },
    {
      name: 'positioning',
      label: 'Pernyataan Positioning Strategis',
      labelRight: (
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 font-normal">Gunakan Teks Auto-Generated</span>
          <SwitchButton
            checked={watched.is_auto_generate}
            onChange={val => setValue('is_auto_generate', val, { shouldDirty: true })}
            disabled={isReadOnly}
          />
        </div>
      ),
      type: 'textarea',
      placeholder: 'Positioning akan di-generate otomatis',
      required: true,
      disabled: watched.is_auto_generate,
    },
  ];

  return (
    <section className="w-full">
      <div className="sticky top-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 p-5 flex flex-col md:flex-row md:items-center justify-between gap-2 shadow-sm">
        <div className="flex flex-col">
          <h1 className="font-semibold text-lg">Positioning</h1>
          <p className="text-sm text-slate-500 dark:text-slate-300 mt-1">
            Anda akan menyusun pembeda bisnis anda dibandingkan kompetitor sejenis.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {!isReadOnly && <AutosaveIndicator status={autosave.status} lastSavedAt={autosave.lastSavedAt} />}

          <button
            type="button"
            onClick={() => setShowPanel(v => !v)}
            className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ChevronRight size={15} className={`transition-transform duration-200 ${showPanel ? 'rotate-90' : ''}`} />
            {showPanel ? 'Sembunyikan Panduan' : 'Tampilkan Panduan'}
          </button>

          {!isReadOnly && (
            <button
              type="button"
              onClick={() => formRef.current?.requestSubmit()}
              disabled={isSaving}
              className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg text-white font-medium transition-opacity disabled:opacity-60"
              style={{ background: '#FD6118' }}
            >
              <Check size={15} />
              {isSaving ? 'Menyimpan...' : 'Simpan & Lanjut'}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-6 p-5">
        <div className="flex-1 min-w-0">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm p-6">
            {autosave.pendingDraft && (
              <DraftBanner
                savedAt={autosave.pendingDraft.savedAt}
                pendingSync={autosave.pendingDraft.pendingSync}
                onRestore={autosave.restoreDraft}
                onDiscard={autosave.discardDraft}
              />
            )}

            <FormProvider {...formControl}>
              <form ref={formRef} onSubmit={handleSubmit(handleSave)}>
                <FormBuilder
                  fields={fields}
                  initialPublished={isPublished}
                  onPublishChange={setIsPublished}
                  hidePublish
                  disabled={isReadOnly}
                  dark
                />
              </form>
            </FormProvider>
          </div>
        </div>

        {showPanel && (
          <div className="w-full xl:w-80 shrink-0 min-w-0">
            <StepTipsPanel tips={tips} videoLink={videoLink} />
          </div>
        )}
      </div>
    </section>
  );
}
